import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { interval, Subscription } from 'rxjs';
import { LoadingService } from './loading.service';
import { environment } from 'src/environments/environment';
const triplinkConfig = environment.tripLinkConfig;

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private tokenCheckInterval: Subscription | null = null;
  private tokenExpiryBuffer = 5 * 60 * 1000; // 5 minutes in milliseconds
//   private scopes = ['user.read']; // Replace with your required scopes
  private scopes = [triplinkConfig.consentScopes[0], 'offline_access'] // Replace with your required scopes

  constructor(
    private authService: MsalService,
    private loadingService: LoadingService
  ) {}

  public initTokenRefreshMonitor(): void {
    // Check token expiry every minute
    this.tokenCheckInterval = interval(60000).subscribe(() => {
      this.checkAndRefreshToken();
    });
  }

  public stopTokenRefreshMonitor(): void {
    if (this.tokenCheckInterval) {
      this.tokenCheckInterval.unsubscribe();
      this.tokenCheckInterval = null;
    }
  }

  private async checkAndRefreshToken(): Promise<void> {
    const account = this.authService.instance.getActiveAccount();
    if (!account) {
      return;
    }

    try {
      // Check if token is about to expire by trying to silently acquire it
      // If it's close to expiration, MSAL will refresh it automatically
      const tokenResponse = await this.authService.instance.acquireTokenSilent({
        account: account,
        scopes: this.scopes,
        forceRefresh: false
      });
      
      // Parse the token to check expiration time
      const tokenClaims = this.parseJwt(tokenResponse.accessToken);
      const expirationTime = tokenClaims.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();

      // If token will expire within the buffer time, refresh it proactively
      if (expirationTime - currentTime < this.tokenExpiryBuffer) {
        console.log('Token will expire soon. Refreshing...');
        this.loadingService.setTokenRefreshInProgress(true);
        await this.authService.instance.acquireTokenSilent({
          account: account,
          scopes: this.scopes,
          forceRefresh: true
        });
        this.loadingService.setTokenRefreshInProgress(false);
      }
    } catch (error) {
      console.error('Token check/refresh failed:', error);
      
      // Reset loader if it gets stuck during token refresh
      this.loadingService.resetLoader();
      
      // If silent refresh fails, redirect to login
      if (error.errorCode === 'consent_required' || 
          error.errorCode === 'interaction_required' || 
          error.errorCode === 'login_required') {
        this.authService.loginRedirect();
      }
    }
  }

  private parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error parsing JWT token', e);
      return { exp: 0 };
    }
  }
}
